/**
 * PDF Generator for Chinese Worksheets
 *
 * This module handles PDF generation using jsPDF with embedded NotoSansTC font
 * for proper Traditional Chinese character rendering.
 *
 * Features:
 * - Multiple grid styles (田字格, 九宮格, 米字格, 空白格)
 * - Tracing guide with fading opacity
 * - Zhuyin, Pinyin, and English annotations
 */

// Grid drawing functions for different styles
const drawGridLines = {
  // 田字格 - Cross grid (default)
  tianZiGe: (doc, x, y, size) => {
    doc.setDrawColor(200)
    doc.setLineWidth(0.1)
    // Horizontal middle line
    doc.line(x, y + size / 2, x + size, y + size / 2)
    // Vertical middle line
    doc.line(x + size / 2, y, x + size / 2, y + size)
  },

  // 九宮格 - 3x3 grid
  jiuGongGe: (doc, x, y, size) => {
    doc.setDrawColor(200)
    doc.setLineWidth(0.1)
    const third = size / 3
    // Horizontal lines
    doc.line(x, y + third, x + size, y + third)
    doc.line(x, y + 2 * third, x + size, y + 2 * third)
    // Vertical lines
    doc.line(x + third, y, x + third, y + size)
    doc.line(x + 2 * third, y, x + 2 * third, y + size)
  },

  // 米字格 - Rice character grid (cross + diagonals)
  miZiGe: (doc, x, y, size) => {
    doc.setDrawColor(200)
    doc.setLineWidth(0.1)
    // Cross lines
    doc.line(x, y + size / 2, x + size, y + size / 2)
    doc.line(x + size / 2, y, x + size / 2, y + size)
    // Diagonal lines
    doc.line(x, y, x + size, y + size)
    doc.line(x + size, y, x, y + size)
  },

  // 空白格 - Plain grid (no internal lines)
  plain: () => {
    // No internal lines
  }
}

const PdfGenerator = {
  /**
   * Generate PDF worksheet
   * @param {Array} characters - Array of character objects { character, zhuyin, pinyin, english }
   * @param {Object} options - Worksheet options
   * @returns {Promise} - Resolves when PDF is downloaded
   */
  generate: async (characters, options) => {
    try {
      // Dynamically import jsPDF and font (lazy loading for performance)
      const [{ jsPDF }, fontModule] = await Promise.all([
        import('jspdf'),
        import('../../../fonts/NotoSansTC-normal')
      ])

      const doc = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: 'a4'
      })

      // Register Chinese font
      doc.addFileToVFS('NotoSansTC-Regular.ttf', fontModule.notoSansTCFont)
      doc.addFont('NotoSansTC-Regular.ttf', 'NotoSansTC', 'normal')

      // Page settings
      const pageWidth = 210
      const pageHeight = 297
      const margin = 15
      const contentWidth = pageWidth - (margin * 2)

      // Grid settings
      const mainGridSize = 25 // Main character box size (mm)
      const smallGridSize = 20 // Tracing box size (mm) - increased from 15 for better visibility
      const gridSpacing = 2 // Space between boxes (mm)
      const rowSpacing = 8 // Space between rows (mm)
      const gridStyle = options.gridStyle || 'tianZiGe'
      const drawGrid = drawGridLines[gridStyle] || drawGridLines.tianZiGe

      // Calculate tracing boxes count
      const tracingCount = options.showTracingGuide ? 3 : 0
      const emptyCount = 3 // Empty practice boxes

      // Title (using Chinese font)
      doc.setFont('NotoSansTC', 'normal')
      doc.setFontSize(18)
      doc.setTextColor(0)
      doc.text('漢字練習單', pageWidth / 2, margin + 5, { align: 'center' })

      // Subtitle with character count
      doc.setFontSize(10)
      doc.setTextColor(100)
      doc.text(`共 ${characters.length} 個漢字`, pageWidth / 2, margin + 11, { align: 'center' })
      doc.setTextColor(0)

      let currentY = margin + 18
      let currentPage = 1

      // Draw each character row
      characters.forEach((char, idx) => {
        // Calculate row height (main box + annotations)
        const annotationHeight = 12
        const rowHeight = mainGridSize + annotationHeight + rowSpacing

        // Check if we need a new page
        if (currentY + rowHeight > pageHeight - margin) {
          doc.addPage()
          currentPage++
          currentY = margin + 5
        }

        let currentX = margin

        // 1. Draw main character box
        doc.setDrawColor(100)
        doc.setLineWidth(0.5)
        doc.rect(currentX, currentY, mainGridSize, mainGridSize)

        // Draw grid lines in main box
        drawGrid(doc, currentX, currentY, mainGridSize)

        // Draw main character
        doc.setFont('NotoSansTC', 'normal')
        doc.setFontSize(18)
        doc.setTextColor(0)
        doc.text(char.character, currentX + mainGridSize / 2, currentY + mainGridSize / 2 + 3, { align: 'center' })

        currentX += mainGridSize + gridSpacing

        // 2. Draw tracing boxes (if enabled)
        if (options.showTracingGuide) {
          // Higher opacity values for clearer tracing characters
          const tracingOpacities = [0.6, 0.4, 0.25]

          for (let i = 0; i < tracingCount; i++) {
            // Draw box border
            doc.setDrawColor(180)
            doc.setLineWidth(0.3)
            doc.rect(currentX, currentY + (mainGridSize - smallGridSize) / 2, smallGridSize, smallGridSize)

            // Draw grid lines in tracing box (apply to all grid styles)
            doc.setDrawColor(220)
            doc.setLineWidth(0.1)
            const boxY = currentY + (mainGridSize - smallGridSize) / 2
            if (gridStyle === 'tianZiGe') {
              doc.line(currentX, boxY + smallGridSize / 2, currentX + smallGridSize, boxY + smallGridSize / 2)
              doc.line(currentX + smallGridSize / 2, boxY, currentX + smallGridSize / 2, boxY + smallGridSize)
            } else if (gridStyle === 'jiuGongGe') {
              const third = smallGridSize / 3
              doc.line(currentX, boxY + third, currentX + smallGridSize, boxY + third)
              doc.line(currentX, boxY + 2 * third, currentX + smallGridSize, boxY + 2 * third)
              doc.line(currentX + third, boxY, currentX + third, boxY + smallGridSize)
              doc.line(currentX + 2 * third, boxY, currentX + 2 * third, boxY + smallGridSize)
            } else if (gridStyle === 'miZiGe') {
              doc.line(currentX, boxY + smallGridSize / 2, currentX + smallGridSize, boxY + smallGridSize / 2)
              doc.line(currentX + smallGridSize / 2, boxY, currentX + smallGridSize / 2, boxY + smallGridSize)
              doc.line(currentX, boxY, currentX + smallGridSize, boxY + smallGridSize)
              doc.line(currentX + smallGridSize, boxY, currentX, boxY + smallGridSize)
            }

            // Draw faded tracing character - larger font for better visibility
            doc.setFont('NotoSansTC', 'normal')
            doc.setFontSize(15) // Increased from 11 for better visibility
            const gray = Math.round(255 * (1 - tracingOpacities[i]))
            doc.setTextColor(gray, gray, gray)
            doc.text(
              char.character,
              currentX + smallGridSize / 2,
              currentY + (mainGridSize - smallGridSize) / 2 + smallGridSize / 2 + 3,
              { align: 'center' }
            )

            currentX += smallGridSize + gridSpacing
          }
        }

        // 3. Draw empty practice boxes
        doc.setTextColor(0)
        for (let i = 0; i < emptyCount; i++) {
          // Draw box border
          doc.setDrawColor(180)
          doc.setLineWidth(0.3)
          doc.rect(currentX, currentY + (mainGridSize - smallGridSize) / 2, smallGridSize, smallGridSize)

          // Draw grid lines in empty box
          doc.setDrawColor(220)
          doc.setLineWidth(0.1)
          if (gridStyle === 'tianZiGe') {
            doc.line(currentX, currentY + (mainGridSize - smallGridSize) / 2 + smallGridSize / 2, currentX + smallGridSize, currentY + (mainGridSize - smallGridSize) / 2 + smallGridSize / 2)
            doc.line(currentX + smallGridSize / 2, currentY + (mainGridSize - smallGridSize) / 2, currentX + smallGridSize / 2, currentY + (mainGridSize - smallGridSize) / 2 + smallGridSize)
          } else if (gridStyle === 'jiuGongGe') {
            const third = smallGridSize / 3
            const boxY = currentY + (mainGridSize - smallGridSize) / 2
            doc.line(currentX, boxY + third, currentX + smallGridSize, boxY + third)
            doc.line(currentX, boxY + 2 * third, currentX + smallGridSize, boxY + 2 * third)
            doc.line(currentX + third, boxY, currentX + third, boxY + smallGridSize)
            doc.line(currentX + 2 * third, boxY, currentX + 2 * third, boxY + smallGridSize)
          } else if (gridStyle === 'miZiGe') {
            const boxY = currentY + (mainGridSize - smallGridSize) / 2
            doc.line(currentX, boxY + smallGridSize / 2, currentX + smallGridSize, boxY + smallGridSize / 2)
            doc.line(currentX + smallGridSize / 2, boxY, currentX + smallGridSize / 2, boxY + smallGridSize)
            doc.line(currentX, boxY, currentX + smallGridSize, boxY + smallGridSize)
            doc.line(currentX + smallGridSize, boxY, currentX, boxY + smallGridSize)
          }

          currentX += smallGridSize + gridSpacing
        }

        // 4. Draw annotations (zhuyin, pinyin, english) below the row
        let annotationY = currentY + mainGridSize + 3
        doc.setFont('NotoSansTC', 'normal')

        if (options.showZhuyin && char.zhuyin) {
          doc.setFontSize(8)
          doc.setTextColor(80)
          doc.text(char.zhuyin, margin, annotationY)
          annotationY += 4
        }

        if (options.showPinyin && char.pinyin) {
          doc.setFontSize(8)
          doc.setTextColor(100)
          doc.text(char.pinyin, margin, annotationY)
          annotationY += 4
        }

        if (options.showEnglish && char.english) {
          doc.setFontSize(7)
          doc.setTextColor(100, 100, 180)
          doc.text(char.english, margin, annotationY)
        }

        doc.setTextColor(0)
        currentY += rowHeight
      })

      // Footer on last page
      doc.setFont('NotoSansTC', 'normal')
      doc.setFontSize(7)
      doc.setTextColor(150)
      doc.text('TutorNetworking 免費教學工具 | tutornetworking.com/tools', pageWidth / 2, pageHeight - 8, { align: 'center' })

      // Download the PDF
      doc.save('chinese-worksheet.pdf')

      return Promise.resolve()
    } catch (error) {
      console.error('PDF generation error:', error)

      // Show user-friendly error message
      alert('PDF 生成失敗，請稍後再試。')

      return Promise.reject(error)
    }
  }
}

export default PdfGenerator
